import type { GetStaticPaths, NextPage } from 'next'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiHandler } from '@hozana/api/ApiHandler'
import { useApiData } from '@hozana/api/hooks/useApiData'
import { createGetStaticData, createGetStaticProps } from '@hozana/api/server/static'
import { ExternalContent } from '@hozana/dom/components/ExternalContent'
import { useRouter } from '@hozana/router'
import { arrayGroupBy } from '@hozana/utils/functions/objects'
import { nl2br } from '@hozana/utils/functions/strings'

import { Container } from 'elements/layout/Container'
import { Space } from 'elements/layout/Space'
import { H } from 'elements/text/H'
import { Link } from 'elements/text/Link'
import { P } from 'elements/text/P'
import { PageTitle } from 'elements/text/PageTitle'
import { Text } from 'elements/text/Text'
import { Separator } from 'elements/ui/Separator'

import { Head } from 'general/structure/Head'
import { Page } from 'general/structure/Page'
import { NAMESPACE } from 'i18n/constants'
import { PAGE } from 'routes/constants'

import { getAllQuestionsQuery } from 'modules/home/queries'

import { FaqCategory } from './FaqCategory'

export const getStaticPaths: GetStaticPaths<{ id: string }> = async () => ({
  paths: [],
  fallback: 'blocking',
})

export const getStaticProps = createGetStaticProps(
  PAGE.FAQ,
  'home:app.page.faq.title',
  async (ctx) => {
    /** Remove this if when custom server is removed */
    if (!ctx.locale) {
      return {
        redirect: {
          permanent: false,
          destination: '/questions-reponses',
        },
      }
    }
    return createGetStaticData(() => ({
      questions: {
        query: getAllQuestionsQuery(),
      },
    }))(ctx)
  },
  {
    namespaces: [NAMESPACE.HOME],
  },
)

export const FaqPage: NextPage = () => {
  const { asPath, pathname, query, replace } = useRouter()
  const { t } = useTranslation()

  useEffect(() => {
    if (asPath.includes(`id=${query.id}`)) {
      replace({ pathname, query }, null, { shallow: true })
    }
  }, [asPath, pathname, query, replace])

  const [questions, questionsQuery] = useApiData(() => ({ query: getAllQuestionsQuery() }), [])

  // Group questions by categories, with the category as the key of each group
  const categoryQuestions = questions && arrayGroupBy(questions, 'category')

  // If an ID is given, return the question with this id, otherwise return the first question of the 'head' category
  const highlightedQuestionId = Number(query.id)
  const highlightedQuestion = questions?.find((question) =>
    highlightedQuestionId ? question.id === highlightedQuestionId : null,
  )

  return (
    <Page>
      <Head isIndexed={false} />
      {highlightedQuestion && (
        <>
          <Container maxW="800px" p={{ xs: '10px', sm: '30px 10px' }}>
            <Space />
            <H fontSize={{ xs: '1.5em', sm: '2em' }} color="darkBlue">
              {highlightedQuestion.title}
            </H>
            <Space />
            <ExternalContent.StaticPage content={highlightedQuestion.answer} imageWithBorder />
            <Space />
          </Container>
          <Separator />
        </>
      )}
      <Container maxW="800px" p={{ xs: '10px', sm: '30px 10px' }}>
        <PageTitle visibleOnMobile align="center" color="darkBlue" m="0 0 1em">
          {nl2br(t('home:app.page.faq.title'))}
        </PageTitle>
        <P m="20px 0" align="center" fontSize="1em">
          <Text>trans:home:app.page.faq.intro.part-one</Text>
          &nbsp;
          <Link color="blue" hoverUnderline to={{ pathname: PAGE.CONTACT }}>
            <Text lowercase bold>
              trans:home:words.contact-us
            </Text>
          </Link>
          &nbsp; &#128512;
        </P>
        <ApiHandler queries={[questionsQuery]} display="block">
          {categoryQuestions &&
            Object.keys(categoryQuestions).map((category) => (
              <FaqCategory questions={categoryQuestions[category]} category={category} key={category} />
            ))}
        </ApiHandler>
        <Space h="100px" />
      </Container>
    </Page>
  )
}
